'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, Package, DollarSign, ShieldAlert, Truck, Printer, CheckCircle } from 'lucide-react';
import { useNotifications, Notification } from '@/hooks/useNotifications';

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function NotificationIcon({ type, metadata }: { type: string; metadata: Record<string, any> }) {
  if (type === 'commission_payout') {
    return <DollarSign size={15} className="text-green-400 shrink-0" />;
  }
  if (type === 'admin_alert') {
    return <ShieldAlert size={15} className="text-red-400 shrink-0" />;
  }
  // order_status — pick icon by status
  const status = metadata?.status || '';
  if (status === 'approved') return <CheckCircle size={15} className="text-brand-400 shrink-0" />;
  if (status === 'printing') return <Printer size={15} className="text-yellow-400 shrink-0" />;
  if (status === 'shipped') return <Truck size={15} className="text-sky-400 shrink-0" />;
  if (status === 'delivered') return <CheckCheck size={15} className="text-green-400 shrink-0" />;
  return <Package size={15} className="text-slate-400 shrink-0" />;
}

function NotificationItem({
  notification,
  onRead,
}: {
  notification: Notification;
  onRead: (id: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => !notification.is_read && onRead(notification.id)}
      className={`w-full text-left flex items-start gap-3 px-4 py-3 transition-colors hover:bg-white/5 ${
        !notification.is_read ? 'bg-brand-600/8' : ''
      }`}
    >
      <div className="mt-0.5 w-7 h-7 rounded-full bg-white/5 flex items-center justify-center shrink-0">
        <NotificationIcon type={notification.type} metadata={notification.metadata} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium leading-snug ${notification.is_read ? 'text-slate-300' : 'text-white'}`}>
          {notification.title}
        </p>
        <p className="text-xs text-slate-400 mt-0.5 leading-snug line-clamp-2">{notification.message}</p>
        <p className="text-xs text-slate-500 mt-1">{timeAgo(notification.created_at)}</p>
      </div>
      {!notification.is_read && (
        <span className="mt-1.5 w-2 h-2 rounded-full bg-brand-500 shrink-0" />
      )}
    </button>
  );
}

interface NotificationBellProps {
  /** 'dark' = sidebar context (white icon), 'light' = topbar context */
  variant?: 'dark' | 'light';
}

export default function NotificationBell({ variant = 'dark' }: NotificationBellProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const { notifications, unreadCount, markAsRead, markAllAsRead, loading } = useNotifications();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const iconColor = variant === 'dark' ? 'text-slate-400 hover:text-white' : 'text-slate-300 hover:text-white';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`relative flex items-center justify-center w-9 h-9 rounded-xl transition-colors hover:bg-white/5 ${iconColor}`}
        aria-label="Notifications"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center min-w-[16px] h-4 px-0.5 rounded-full bg-brand-600 text-white text-[10px] font-bold leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-2xl border border-white/10 bg-navy-800/98 backdrop-blur-md shadow-2xl z-[100] overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-brand-400" />
              <span className="text-sm font-semibold text-white">Notifications</span>
              {unreadCount > 0 && (
                <span className="flex items-center justify-center min-w-[18px] h-4.5 px-1 rounded-full bg-brand-600 text-white text-[10px] font-bold">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllAsRead}
                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
                  title="Mark all as read"
                >
                  <CheckCheck size={13} />
                  All read
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-500 hover:text-white hover:bg-white/5 transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[360px] overflow-y-auto scrollbar-thin divide-y divide-white/5">
            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-slate-500">Loading…</div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell size={28} className="mx-auto text-slate-600 mb-2" />
                <p className="text-sm text-slate-500">No notifications yet</p>
              </div>
            ) : (
              notifications.map((n) => (
                <NotificationItem key={n.id} notification={n} onRead={markAsRead} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
